/* eslint-disable react/jsx-one-expression-per-line */
/* eslint-disable react/no-unescaped-entities */
import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import {
  LANDOWNER_INSURANCE_TERMS_VERSION,
  LANDOWNER_INSURANCE_TERMS_LAST_UPDATED,
} from '../../util/landownerInsuranceTerms';

import css from './LandownerInsuranceTerms.module.css';

const LandownerInsuranceTerms = props => {
  const { rootClassName, className } = props;
  const classes = classNames(rootClassName || css.root, className);

  return (
    <div className={classes}>
      <h1>LANDOWNER INSURANCE TERMS</h1>

      <p className={css.draftBanner}>
        <strong>Draft — pending legal review.</strong> This interim version is provisional and
        subject to change. Final counsel-approved terms will replace this body and bump the version
        number.
      </p>

      <p className={css.meta}>
        <strong>Version:</strong> {LANDOWNER_INSURANCE_TERMS_VERSION}
      </p>
      <p className={classNames(css.meta, css.metaSpacing)}>
        <strong>Last Updated:</strong> {LANDOWNER_INSURANCE_TERMS_LAST_UPDATED}
      </p>

      <h2>1. Agreement</h2>
      <p>
        By enrolling in LandTrust Landowner Insurance and completing payment (or redeeming a valid
        promotional offer that entitles you to coverage), you accept these Landowner Insurance Terms
        in addition to the LandTrust <a href="/terms-of-service">Terms of Service</a>. If you do not
        agree, do not enroll.
      </p>

      <h2>2. Program overview</h2>
      <p>
        LandTrust Landowner Insurance is an annual coverage program for landowners who list property
        on the LandTrust Platform. When enrollment is active, coverage relates to bookings made
        through the LandTrust Platform for your Listings. The fee is the then-current annual price
        unless a valid promotional code or other entitlement applies.
      </p>

      <h2>3. What coverage is for</h2>
      <p>
        Subject to the master policy, policy limits, exclusions, and carrier terms, the program is
        intended to provide liability protection related to guest use of your land for booked
        LandTrust trips. A non-controlling summary of coverage themes may appear on the{' '}
        <a href="/insurance">LandTrust Insurance</a> page. That marketing summary does not modify or
        expand these Terms or the underlying policy.
      </p>

      <h2>4. What it is not</h2>
      <p>
        Landowner Insurance is not a personal homeowner or farmowners policy. Without limitation, it
        does not cover:
      </p>
      <ul className={css.disc}>
        <li>Bookings or activities arranged outside the LandTrust Platform</li>
        <li>Guest or third-party activity that is not part of a booked LandTrust trip</li>
        <li>
          Conduct that violates LandTrust Platform rules, applicable law, or the terms of your
          Listing (including themes addressed in the Terms of Service regarding prohibited uses and
          landowner responsibilities)
        </li>
      </ul>

      <h2>5. Enrollment and payment</h2>
      <p>
        Coverage begins when enrollment is active after successful payment, or when you become
        entitled through a valid promotional offer. Subscriptions renew annually via Stripe (or
        LandTrust's then-current payment processor) until canceled in accordance with LandTrust's
        process. Promotional codes may reduce or waive the fee for a stated period; entitlement
        after a promo ends is subject to payment of the then-current fee unless otherwise stated.
      </p>

      <h2>6. Your responsibilities</h2>
      <p>You agree to:</p>
      <ul className={css.disc}>
        <li>Keep Listing information accurate and up to date</li>
        <li>Follow LandTrust booking, safety, and Platform rules</li>
        <li>Cooperate reasonably with claims investigation and administration</li>
        <li>
          Maintain any personal or property coverage that you or LandTrust reasonably recommend or
          that applicable law requires, independent of this program
        </li>
      </ul>

      <h2>7. Claims</h2>
      <p>
        Report potential claims promptly through the process LandTrust or the carrier provides.
        Coverage determinations rest with the insurer under the master policy. LandTrust may
        facilitate communication but does not decide coverage outcomes.
      </p>

      <h2>8. Changes</h2>
      <p>
        LandTrust may update these Landowner Insurance Terms from time to time. Continued enrollment
        after notice, or acceptance of a new version on renewal or re-enrollment, may be required.
        The version you accepted is stored on your account (for example, under insurance profile
        metadata) together with the time of acceptance.
      </p>

      <h2>9. Limitation and disclaimer</h2>
      <p>
        Coverage is subject to the master policy and carrier terms. LandTrust is not itself the
        insurer. The program may change, be suspended, or end with reasonable notice. To the fullest
        extent permitted by law, LandTrust's role is limited to offering and administering
        enrollment in the program; claims and coverage remain between you and the insurer as set out
        in the policy.
      </p>

      <h2>10. Contact</h2>
      <p>
        For questions about enrollment or coverage, contact us at{' '}
        <a href="mailto:support@landtrust.com">support@landtrust.com</a>.
      </p>
    </div>
  );
};

LandownerInsuranceTerms.defaultProps = {
  rootClassName: null,
  className: null,
};

const { string } = PropTypes;

LandownerInsuranceTerms.propTypes = {
  rootClassName: string,
  className: string,
};

export default LandownerInsuranceTerms;
